import React, { useEffect, useState } from "react";
import { Image as ImageIcon, Save, Trash2, Upload } from "lucide-react";
import { toast } from "react-hot-toast";
import api, { getPlatformLogo, updateWelcomeImage } from "../services/api";

/**
 * Master admin: the image shown above "Welcome" on the home page
 * (eduw.in). Without one, the home page shows the platform logo.
 */
const WelcomeImageSettings: React.FC = () => {
  const [savedUrl, setSavedUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPlatformLogo()
      .then((res) => {
        const url = res.data?.welcomeImage || "";
        setSavedUrl(url);
        setPreviewUrl(url);
      })
      .catch(() => {});
  }, []);

  const onSelect = (selected?: File) => {
    if (!selected) return;
    if (!selected.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };

  const save = async (url: string, message: string) => {
    await updateWelcomeImage(url);
    setSavedUrl(url);
    setPreviewUrl(url);
    setFile(null);
    toast.success(message);
  };

  const onSave = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const body = new FormData();
      body.append("image", file);
      const res = await api.post<{ url: string }>("/upload-image", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await save(res.data.url, "Welcome image updated");
    } catch (err: any) {
      toast.error(err.response?.data?.msg || err.response?.data?.error || "Failed to save image");
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove the welcome image? The home page will show the logo instead.")) return;
    setSaving(true);
    try {
      await save("", "Welcome image removed");
    } catch {
      toast.error("Failed to remove image");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mt-6">
      <div className="flex items-center gap-3 mb-4">
        <ImageIcon size={28} className="text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-800 m-0">Welcome Page Image</h1>
      </div>
      <p className="text-gray-600 mb-6">
        Shown above “Welcome” on the home page, where everyone picks Admin, POS, Sales or Kitchen.
      </p>

      <div className="mb-4 flex h-44 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
        {previewUrl ? (
          <img src={previewUrl} alt="Welcome" className="max-h-40 max-w-full object-contain" />
        ) : (
          <span className="text-sm text-gray-400">No image — the home page shows the logo</span>
        )}
      </div>

      <label className="mb-4 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-300 px-4 py-3 text-blue-600 hover:bg-blue-50">
        <Upload size={18} />
        {file ? `Selected: ${file.name}` : "Click to select an image"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => onSelect(e.target.files?.[0])} />
      </label>

      <div className="flex gap-3">
        <button
          onClick={onSave}
          disabled={saving || !file}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Save size={20} />
          {saving ? "Saving..." : "Save Image"}
        </button>
        <button
          onClick={onRemove}
          disabled={saving || !savedUrl}
          className="flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 size={20} />
          Remove
        </button>
      </div>
    </div>
  );
};

export default WelcomeImageSettings;
